import client from '../../api';
import axios from 'axios';
import qs from 'qs';
import { localToUTCStringForDateRange } from '../../../utility/dates/localToUTCForDateRange';

const CancelToken = axios.CancelToken;

// Each request type keeps its own cancel handle. Sharing a single handle
// across endpoints meant that firing one request (e.g. user groups) would
// cancel an unrelated in-flight request (e.g. individual users), surfacing
// as an uncaught CanceledError. Keyed handles ensure a request only cancels
// the previous request of the SAME type.
const cancelHandles = {};

const cancelableGet = async (key, url, config) => {
  if (cancelHandles[key]) {
    cancelHandles[key]();
  }

  try {
    return await client.get(url, {
      ...config,
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
      cancelToken: new CancelToken((c) => {
        cancelHandles[key] = c;
      }),
    });
  } catch (error) {
    // A superseded request rejects with a CanceledError; swallow it so callers
    // (which check isResponseOk) simply ignore the stale result instead of
    // crashing on an unhandled rejection.
    if (axios.isCancel(error)) {
      return undefined;
    }
    throw error;
  }
};

export const getFacilities = async ({ companyId }) => {
  return cancelableGet('facilities', `/api/corporate-library/report-builder/statistics/facilities/`, {
    params: { company_id: companyId },
  });
};
export const getUserGroups = async ({ companyId, facilitiesIDs }) => {
  return cancelableGet('userGroups', `/api/corporate-library/report-builder/statistics/user-groups/`, {
    params: { company_id: companyId, facilities_ids: facilitiesIDs },
  });
};
export const getFormsList = async ({
  companyId,
  facilitiesIDs,
  initiationDateAfter,
  initiationDateBefore,
  userGroupsIDs,
  usersIds,
  folderId,
  search,
  tree,
}) => {
  const params = {
    company_id: companyId,
  };

  // Which of the two form trees the level belongs to: the corporate library one by
  // default, the Tasks page one when named. A folder id alone cannot say -- the two
  // number their folders apart.
  if (tree) params.tree = tree;

  if (facilitiesIDs) params.facilities_ids = facilitiesIDs;
  if (initiationDateAfter) params.initiation_date_after = localToUTCStringForDateRange(initiationDateAfter, 'start');
  if (initiationDateBefore) {
    params.initiation_date_before = localToUTCStringForDateRange(initiationDateBefore, 'end');
  }
  if (userGroupsIDs) params.user_groups_ids = userGroupsIDs;
  // The "Individual users" widen the report beyond the user groups, so a form that
  // reached only them in the period is offered too.
  if (usersIds?.length) params.users_ids = usersIds;
  if (folderId !== undefined && folderId !== null) params.folder_id = folderId;
  if (search) params.search = search;

  return cancelableGet('formsList', `/api/corporate-library/report-builder/statistics/forms/`, { params });
};

export const getUsers = async (params) => {
  return cancelableGet('users', `/api/corporate-library/report-builder/statistics/individual-users/`, { params });
};

export const getReport = async ({
  companyId,
  formsIDs,
  tasksIds,
  facilitiesIDs,
  initiationDateAfter,
  initiationDateBefore,
  userGroupsIDs,
  userStatus,
  selectedMonthValue,
  usersIds,
}) => {
  if (cancelHandles.report) {
    cancelHandles.report();
  }

  const params = {
    company_id: companyId,
    forms_ids: formsIDs,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };

  // The tasks reported next to the forms, each as a whole.
  if (tasksIds?.length) params.tasks_ids = tasksIds;
  if (facilitiesIDs) params.facilities_ids = facilitiesIDs;
  if (initiationDateAfter) params.initiation_date_after = localToUTCStringForDateRange(initiationDateAfter, 'start');
  if (initiationDateBefore) {
    params.initiation_date_before = localToUTCStringForDateRange(initiationDateBefore, 'end');
  }
  if (userGroupsIDs) params.user_groups_ids = userGroupsIDs;
  if (userStatus) params.user_status = userStatus;
  if (selectedMonthValue) params.hire_month = selectedMonthValue;
  if (usersIds) params.users_ids = usersIds;

  try {
    return await client.post(`/api/corporate-library/report-builder/statistics/download/`, params, {
      responseType: 'blob',
      cancelToken: new CancelToken(function executor(c) {
        cancelHandles.report = c;
      }),
    });
  } catch (e) {
    console.log(e);
  }
};

export const getFacilitiesReport = async ({ companyId, formsIDs, tasksIds, facilitiesIDs, initiationDateAfter, initiationDateBefore }) => {
  if (cancelHandles.facilitiesReport) {
    cancelHandles.facilitiesReport();
  }

  const params = {
    company_id: companyId,
    forms_ids: formsIDs,
    facilities_ids: facilitiesIDs,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };

  if (tasksIds?.length) params.tasks_ids = tasksIds;
  if (initiationDateAfter) params.initiation_date_after = localToUTCStringForDateRange(initiationDateAfter, 'start');
  if (initiationDateBefore) {
    params.initiation_date_before = localToUTCStringForDateRange(initiationDateBefore, 'end');
  }

  try {
    return await client.post(`/api/corporate-library/report-builder/statistics/download-facilities/`, params, {
      responseType: 'blob',
      cancelToken: new CancelToken(function executor(c) {
        cancelHandles.facilitiesReport = c;
      }),
    });
  } catch (e) {
    console.log(e);
  }
};

export const getFolderFormIds = async ({
  companyId,
  folderId,
  facilitiesIDs,
  initiationDateAfter,
  initiationDateBefore,
  userGroupsIDs,
  usersIds,
  tree,
}) => {
  const params = {
    company_id: companyId,
    folder_id: folderId,
  };

  if (tree) params.tree = tree;

  if (facilitiesIDs) params.facilities_ids = facilitiesIDs;
  if (initiationDateAfter) params.initiation_date_after = localToUTCStringForDateRange(initiationDateAfter, 'start');
  if (initiationDateBefore) {
    params.initiation_date_before = localToUTCStringForDateRange(initiationDateBefore, 'end');
  }
  if (userGroupsIDs) params.user_groups_ids = userGroupsIDs;
  if (usersIds?.length) params.users_ids = usersIds;

  return await client.get(`/api/corporate-library/report-builder/statistics/folder-form-ids/`, {
    params,
    paramsSerializer: (params) => {
      return qs.stringify(params, { arrayFormat: 'repeat' });
    },
  });
};
